import type {
	Parameters,
	ExtensionManifest,
	DefaultExtensionProvider,
} from '@atlaskit/editor-common/extensions';

import type { CustomSearchExtensionProps } from '@atlassian/custom-search-extension';

import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';

import { linkCardsExtensionProvider } from './link-cards/edit/LinkCardsManifest';
import { carouselExtensionProvider } from './carousel/carouselManifest';
import { spotlightExtensionProvider } from './spotlight/spotlightManifest';
import { linksMenuExtensionProvider } from './links-menu/linksMenuManifest';
import { sectionExtensionProvider } from './section/sectionManifest';
import { hubElementPlaceholderExtensionProvider } from './hub-element-placeholder/hubElementPlaceholderManifest';
import type { CustomSitesExtensionProviderProps } from './shared-components/customSitesManifestBuilder';
import { ConfluenceDependenciesProvider } from './platform-props-providers/ConfluenceDependenciesProvider';

const platformSearchExtensionProviderLoader = async (props: CustomSearchExtensionProps) => {
	const module = await import(
		/* webpackChunkName: "loadable-platformSearchExtensionProvider" */ '@atlassian/custom-search-extension'
	);
	return module.searchExtensionProvider(props);
};

const searchExtensionProviderLoader = async (props: CustomSitesExtensionProviderProps) => {
	const module = await import(
		/* webpackChunkName: "loadable-searchExtensionProvider" */ './search/searchManifest'
	);
	return module.searchExtensionProvider(props);
};

export const createPremiumExtensionManifests = async (
	props: CustomSitesExtensionProviderProps,
): Promise<ExtensionManifest<Parameters>[]> => {
	const extensionProviders = listPremiumExtensionProviders(
		props.contentId,
		props.allowCompanyHubExclusiveExtensions,
	);

	const premiumExtensions: ExtensionManifest<Parameters>[] = [];

	for (const provider of extensionProviders) {
		try {
			const instance = await provider(props);
			const manifests = await instance.getExtensions();
			premiumExtensions.push(...manifests);
		} catch (error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMPANY_HUB,
			});
		}
	}

	return premiumExtensions;
};

type ExtensionProvider = (
	props: CustomSitesExtensionProviderProps,
) => Promise<DefaultExtensionProvider<Parameters>>;

function listPremiumExtensionProviders(
	contentId: string,
	allowCompanyHubExclusiveExtensions?: boolean,
) {
	const extensionProviders: ExtensionProvider[] = [
		spotlightExtensionProvider,
		carouselExtensionProvider,
		linkCardsExtensionProvider,
	];

	if (allowCompanyHubExclusiveExtensions) {
		extensionProviders.push(linksMenuExtensionProvider);

		if (fg('company_hub_section_extension')) {
			extensionProviders.push(sectionExtensionProvider);
			extensionProviders.push(hubElementPlaceholderExtensionProvider);
		}
		if (fg('company_hub_search_extension')) {
			if (fg('company_hub_search_extension_platformization')) {
				extensionProviders.push(async (providerProps) => {
					return (
						await platformSearchExtensionProviderLoader({
							contentId,
							mode: 'editor',
							propsProvider: ConfluenceDependenciesProvider,
						})
					)(providerProps);
				});
			} else {
				extensionProviders.push(searchExtensionProviderLoader);
			}
		}
	}

	return extensionProviders;
}
